import { Typography, Box, Button, Modal, useTheme, useMediaQuery } from '@mui/material';
import * as React from 'react'
import { Draft, Team } from '../../../../sdk/model';
import { getColorByPosition } from '../../../util/ColorByPosition'
import PlayerInfoCard from '../../../cards/playerCard'

interface draftBoardProps {
  draft: Draft | undefined,
  playerMap: Map<string | undefined, Player | undefined> | undefined,
  teamMap: Map<string | undefined, Team>
}

function DraftBoard(props: draftBoardProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [viewingPlayer, setViewingPlayer] = React.useState(undefined);
  
  const renderPick = (roundNumber: number, pick: any, pickNumber: number) => {
    const selectedPlayer = pick?.contract?.playerId ? props?.playerMap?.get(pick.contract.playerId) : null;
    const teamName = props.teamMap.get(pick?.teamId)?.name;
    const originalTeamName = pick.teamId !== pick.originalTeamId ? `(via ${props.teamMap.get(pick.originalTeamId)?.name})` : '';

    return (
      <Button 
        variant="contained" 
        onClick={() => selectedPlayer && setViewingPlayer(selectedPlayer)}
        sx={{ 
          width: isMobile ? 80 : 110, 
          height: isMobile ? 90 : 100, 
          overflow: 'hidden', 
          m: 0.5,
          p: 0.5,
          display: 'flex',
          flexDirection: 'column',
          ...getColorByPosition(selectedPlayer?.position || null)
        }}
      >
        <Box sx={{ width: '100%', textAlign: 'left', mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontSize: 10, color: 'black', lineHeight: 1 }}>
            {roundNumber}.{String(pickNumber).padStart(2, '0')}
          </Typography>
        </Box>
        
        {selectedPlayer ? (
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ fontSize: isMobile ? 12 : 14, lineHeight: 1.1, fontWeight: 'bold' }}>
                {selectedPlayer.first_name}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: isMobile ? 12 : 14, lineHeight: 1.1, fontWeight: 'bold' }}>
                {selectedPlayer.last_name}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 'bold' }}>
                {selectedPlayer.position} - {selectedPlayer.nfl_team?.alias || 'FA'}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="caption" sx={{ fontSize: 10, color: 'black' }}>
              {teamName}
            </Typography>
          </Box>
        )}
        
        <Box sx={{ width: '100%' }}>
          <Typography variant="caption" sx={{ 
            fontSize: 8, 
            color: 'black',
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {teamName} {originalTeamName}
          </Typography>
        </Box>
      </Button>
    );
  };
  
  return (
    <Box>
      <Modal 
        open={!!viewingPlayer} 
        onClose={() => setViewingPlayer(undefined)} 
        aria-labelledby="modal-modal-title" 
        aria-describedby="modal-modal-description"
        sx={{ margin: '5%' }}
      >
        <PlayerInfoCard player={viewingPlayer} leagueId={props.draft?.leagueId}/>
      </Modal>
      
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: 1,
        overflowX: 'auto',
        pb: 1
      }}>
        {Object.entries(props.draft?.draftPicks || {}).map(([roundIndex, round]) => {
          const roundNumber = Number(roundIndex) + 1;
          return (
            <Box key={roundIndex} sx={{ 
              display: 'flex',
              gap: 0.5,
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              {Object.entries(round).map(([pickIndex, pick]) => {
                const pickNumber = Number(pickIndex) + 1;
                return (
                  <Box key={pickIndex}>
                    {renderPick(roundNumber, pick, pickNumber)}
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default DraftBoard;